import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRealEstateAddress,
  setSelectedRealEstateAddress,
  setRealEstatePresetsSelectedRow,
  setSelectedRow,
} from "../../store/actions/uiActions/myPermitsActions";
import {
  setRealEstatePresetsPermitPackage,
  updatePermitPackage,
  setQuickActionAddress,
} from "../../store/actions/dataActions";
import { getFormInitials } from "../../store/actions/permitFormActions/buildingStateActions";
import { isPropertyDatabaseCounty } from "../../functions/availablePermitForms";
import { buildRealEstateSearchQuery } from "../../utils/suffix";
import axios from "axios";

const PropertyAddressFetchModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);
  const realEstateSet = selectedRow?.realEstatePresets?.realEstateSet;
  const selectedPreset = selectedRow?.realEstatePresets?.selectedAddress;
  const { addressOptions, fetchLoading } = useSelector(
    (state) => state.myPermitsComponent.realEstate,
  );

  const { address: tempAddress } = useSelector(
    (state) => state.nocData.quickActions,
  );

  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const debounceTimerRef = useRef(null);
  const lastFetchedAddressRef = useRef("");
  const suppressFetchRef = useRef(false);
  const tempAddressRef = useRef(null);

  // Format address for display
  const formatAddressOption = useCallback((option) => {
    if (!option || typeof option === "string") return option || "";
    const { city, zip_code, full_address } = option;

    return `${full_address}, ${city}, ${zip_code}`.trim();
  }, []);

  const getOptionParcelId = useCallback((option) => {
    if (!option || typeof option === "string") return "";
    return String(option.parcel_id || option.parcel_number || "").trim();
  }, []);

  const findMatchingOptionByParcelId = useCallback(
    (options, parcelId) => {
      const normalizedParcelId = String(parcelId || "").trim();
      if (!normalizedParcelId || !Array.isArray(options)) return null;

      return (
        options.find(
          (option) => getOptionParcelId(option) === normalizedParcelId,
        ) || null
      );
    },
    [getOptionParcelId],
  );

  // Fetch addresses when user types
  useEffect(() => {
    const searchQuery = buildRealEstateSearchQuery(inputValue);

    // Only fetch if modal is open
    if (!open) return;

    if (window.__suppressRealEstateFetch) {
      window.__suppressRealEstateFetch = false;
      return;
    }

    if (!searchQuery) return;

    if (searchQuery === lastFetchedAddressRef.current) return;
    lastFetchedAddressRef.current = searchQuery;

    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      dispatch(fetchRealEstateAddress(searchQuery));
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, dispatch, open]);

  // Prefill address from quickActions (dashboard quick actions)
  useEffect(() => {
    if (!open) return; // Only run when modal is open

    const handleFetchRealEstate = async () => {
      if (!tempAddress?.fullStreetAddress) return;

      const searchQuery = buildRealEstateSearchQuery(
        tempAddress.fullStreetAddress,
      );

      if (!searchQuery) return;

      const options = await dispatch(fetchRealEstateAddress(searchQuery));

      if (!Array.isArray(options) || options.length === 0) {
        await dispatch(
          setQuickActionAddress({
            fullStreetAddress: "",
            houseNumber: "",
            streetName: "",
            parcelID: "",
          }),
        );
        return;
      }

      const formattedInput = tempAddress.fullStreetAddress.toUpperCase();
      // Suppress the manual input fetch to prevent infinite loop
      suppressFetchRef.current = true;
      setInputValue(formattedInput);

      const matchedOption = findMatchingOptionByParcelId(
        options,
        tempAddress?.parcelID,
      );

      if (matchedOption || options.length === 1) {
        const option = matchedOption || options[0];
        setSelectedOption(option);
        dispatch(setSelectedRealEstateAddress(option));
      } else {
        // Multiple options - user can select from dropdown
        // The options are already in addressOptions from the fetch
      }

      // Clean up temp quickAction so it doesn't repeat
      await dispatch(
        setQuickActionAddress({
          fullStreetAddress: "",
          houseNumber: "",
          streetName: "",
          parcelID: "",
        }),
      );
    };

    // Run if tempAddress exists and we haven't processed it yet for this modal session
    if (tempAddress?.fullStreetAddress && !tempAddressRef?.current) {
      tempAddressRef.current = true;
      handleFetchRealEstate();
    }
  }, [
    tempAddress?.fullStreetAddress,
    tempAddress?.parcelID,
    dispatch,
    findMatchingOptionByParcelId,
    open,
  ]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSelectedOption(null);
      setSaveLoading(false);
      lastFetchedAddressRef.current = "";
      tempAddressRef.current = null; // Reset ref so it can run again next time
    }
  }, [open]);

  // Handle address selection
  const handleAddressChange = (e, value) => {
    suppressFetchRef.current = true;
    setSelectedOption(value);
    setInputValue(value ? formatAddressOption(value) : "");
    dispatch(setSelectedRealEstateAddress(value));
  };

  // Save property data to permit package
  const handleSavePropertyData = async () => {
    if (!selectedOption) return;

    try {
      setSaveLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/real-estate/preset/${selectedRow?.county.toLowerCase().replace(" ", "_")}`,
        {
          currentSession: currentSession,
          task_id: selectedRow?.task_id,
          selected_address: selectedOption,
          skip_noc: true, // Flag to indicate this is not for NOC creation
        },
        {
          withCredentials: true,
        },
      );

      if (res?.data?.success) {
        await dispatch(
          setRealEstatePresetsSelectedRow({
            data: res?.data?.data,
            address: selectedOption,
          }),
        );
        await dispatch(
          setRealEstatePresetsPermitPackage({
            realEstatePresets: res?.data?.data,
            task_id: selectedRow?.task_id,
          }),
        );

        // Backend now returns the fully updated permit package with common_fields, property_owner, and project_address
        const updatedPermitPackage = res?.data?.data;

        // Use the backend's updated data directly
        const updatedSelectedRow = {
          ...selectedRow,
          ...updatedPermitPackage,
        };

        // Update the row in the grid/list (permitPackages array)
        await dispatch(updatePermitPackage(updatedSelectedRow));

        // Update the selectedRow in Redux
        await dispatch(setSelectedRow(updatedSelectedRow));

        // Refresh initials so forms pick up updated document values
        await dispatch(getFormInitials());

        // Force re-mount of the form so controls rebind to updated values
        await dispatch({ type: "BUMP_FORM_BIND_VERSION" });

        // Call onSuccess callback
        if (onSuccess) {
          onSuccess(res?.data?.data);
        }

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error("Error saving property data:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle skip
  const handleSkip = () => {
    onClose();
  };

  // Check if county supports property database
  const isSupportedCounty = isPropertyDatabaseCounty(selectedRow?.county);

  // Don't render modal if county is not supported
  if (!isSupportedCounty) {
    return null;
  }

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
        Fetch Property Details
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Would you like to fetch property details to auto-populate your
            permit forms? This will retrieve owner information, property
            address, and other relevant details from the{" "}
            <strong>
              {selectedRow?.county === "Duval"
                ? "Duval County Property Appraiser"
                : selectedRow?.county === "St Johns" ||
                    selectedRow?.county === "St_Johns"
                  ? "St. Johns County Property Appraiser"
                  : selectedRow?.county === "Polk"
                    ? "Polk County Property Appraiser"
                    : `${selectedRow?.county || "County"} Property Appraiser`}
            </strong>{" "}
            database.
          </Typography>

          <Autocomplete
            options={addressOptions || []}
            loading={fetchLoading}
            value={realEstateSet ? selectedPreset : selectedOption}
            onChange={handleAddressChange}
            inputValue={
              inputValue !== ""
                ? inputValue
                : realEstateSet
                  ? formatAddressOption(selectedPreset)
                  : selectedOption
                    ? formatAddressOption(selectedOption)
                    : ""
            }
            onInputChange={(e, newInputValue) => {
              setInputValue(newInputValue);
            }}
            getOptionLabel={(option) => formatAddressOption(option)}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;
              if (typeof option === "string" || typeof value === "string") {
                return option === value;
              }

              const optionId = option.parcel_id || option.parcel_number;
              const valueId = value.parcel_id || value.parcel_number;

              return optionId === valueId;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Property Address"
                placeholder="Start typing an address..."
                variant="filled"
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fetchLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
            renderOption={(props, option, { index }) => (
              <li {...props} key={`${option.parcel_number}-${index}`}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatAddressOption(option)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Owner: {option.owner_name || "N/A"} | Parcel:{" "}
                    {option.parcel_id_display ||
                      option.parcel_number_display ||
                      option.parcel_number ||
                      option.parcel_id ||
                      "N/A"}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText={
              inputValue.trim()
                ? "No properties found. Try a different address."
                : "Start typing to search for properties..."
            }
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Tip: Enter a street address like "123 Main St" to search for
            properties
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkip} disabled={saveLoading}>
          Skip for Now
        </Button>
        <LoadingButton
          onClick={handleSavePropertyData}
          loading={saveLoading}
          disabled={!selectedOption}
          variant="contained"
          color="primary"
        >
          Fetch & Apply Property Details
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(PropertyAddressFetchModal);
